'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/components/ui/utils';

const CHECKBOX_CLS =
  'mt-0.5 size-[15px] rounded-[3px] border-black/25 data-[state=checked]:border-[#E31E24] data-[state=checked]:bg-[#E31E24]';

interface FormConsentCheckboxesProps {
  idPrefix: string;
  privacyConsent: boolean;
  onPrivacyConsentChange: (checked: boolean) => void;
  marketingConsent?: boolean;
  onMarketingConsentChange?: (checked: boolean) => void;
  /** When true, shows the SMS / RCS / WhatsApp marketing consent checkbox. */
  showMarketing?: boolean;
  /** Tighter spacing and text for constrained panels (e.g. sales callback widget). */
  compact?: boolean;
  className?: string;
  textClassName?: string;
  linkClassName?: string;
  /** Optional Strapi rich text for the privacy consent row. */
  privacyHtml?: string | null;
  /** Optional CMS copy for the marketing consent row. */
  marketingLabel?: string | null;
}

export default function FormConsentCheckboxes({
  idPrefix,
  privacyConsent,
  onPrivacyConsentChange,
  marketingConsent = false,
  onMarketingConsentChange,
  showMarketing = false,
  compact = false,
  className,
  textClassName,
  linkClassName = 'font-semibold text-[#E31E24] hover:underline',
  privacyHtml,
  marketingLabel,
}: FormConsentCheckboxesProps) {
  const resolvedTextClass =
    textClassName ??
    (compact ? 'text-[11px] leading-snug text-[#333]' : 'text-[12px] leading-relaxed text-[#333]');

  return (
    <div className={cn(compact ? 'space-y-2.5' : 'space-y-3.5', className)}>
      {showMarketing && onMarketingConsentChange && (
        <label className={cn('flex cursor-pointer items-start', compact ? 'gap-2.5' : 'gap-3')}>
          <Checkbox
            id={`${idPrefix}-marketing`}
            checked={marketingConsent}
            onCheckedChange={(checked) => onMarketingConsentChange(checked === true)}
            className={CHECKBOX_CLS}
          />
          <span className={resolvedTextClass}>
            {marketingLabel?.trim() ||
              'I agree to receive communications on newsletters, promotional content, offers and events through SMS, RCS and WhatsApp.'}
          </span>
        </label>
      )}

      <label className={cn('flex cursor-pointer items-start', compact ? 'gap-2.5' : 'gap-3')}>
        <Checkbox
          id={`${idPrefix}-privacy`}
          checked={privacyConsent}
          onCheckedChange={(checked) => onPrivacyConsentChange(checked === true)}
          className={CHECKBOX_CLS}
        />
        {privacyHtml?.trim() ? (
          <span
            className={cn(
              resolvedTextClass,
              '[&_p]:m-0 [&_a]:font-semibold [&_a]:text-[#E31E24] [&_a]:hover:underline',
            )}
            dangerouslySetInnerHTML={{ __html: privacyHtml }}
            onClick={(event) => {
              if ((event.target as Element).closest('a')) event.stopPropagation();
            }}
          />
        ) : (
          <span className={resolvedTextClass}>
            I consent to Wonder Cement Limited collecting and processing the personal information I
            have provided above to respond to my enquiry, as described in the{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </a>
            . I understand I may withdraw this consent at any time by contacting{' '}
            <a
              href="mailto:privacy@wondercement.com"
              className={linkClassName}
              onClick={(e) => e.stopPropagation()}
            >
              privacy@wondercement.com
            </a>
            .
          </span>
        )}
      </label>
    </div>
  );
}
